import { Search, Loader2 } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import type { SearchGroup, SearchItem } from "@/hooks/useGlobalSearch";
import { useSearchMeta, buildSearchPlaceholder } from "@/hooks/useSearchMeta";

/**
 * Topbar global search.
 *
 * Two presentations off one shared state:
 *  - md+ → inline input that stretches across the topbar, dropdown rendered
 *    via a React portal into document.body so PortalLayout's overflow-hidden
 *    wrapper can't clip it.
 *  - <md → icon-only trigger that opens a Dialog containing the same search
 *    UI. Results render inline inside the dialog (no portal needed — Radix
 *    already gives the dialog its own stacking context).
 *
 * `value` / `activeIndex` are shared, so the experience and the
 * keyboard navigation behave identically on both surfaces.
 */
export const GlobalSearch = () => {
  const [value, setValue] = useState("");
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Gates the mobile field's readOnly guard (see desktop input) — starts
  // readOnly so autofill skips it, cleared once the user actually focuses.
  const [mobileFocused, setMobileFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelRect, setPanelRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const desktopInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const { enabled, isLoading, isError, errorMessage, groups, isEmpty } = useGlobalSearch(value);
  const { data: metaData } = useSearchMeta();
  const placeholder = buildSearchPlaceholder(metaData?.items);

  // Flat index across groups so ↑/↓ can roam freely.
  const flatItems = useMemo(() => {
    const acc: { group: SearchGroup; item: SearchItem; flatIndex: number }[] = [];
    groups.forEach((g) => {
      g.items.forEach((i) => {
        acc.push({ group: g, item: i, flatIndex: acc.length });
      });
    });
    return acc;
  }, [groups]);

  const desktopShowPanel = desktopOpen && enabled;

  // Collapsed to an icon-sized box when idle; expands once it's focused or
  // there's a query to show. Drives both the width animation and which
  // affordances (placeholder, clear-padding, cursor) are visible.
  const desktopExpanded = desktopOpen || value.length > 0;

  // Position the desktop dropdown to viewport coords whenever it might move:
  // when it first opens, when the input reflows, or when the user scrolls /
  // resizes. useLayoutEffect avoids a one-frame jump at stale coords.
  useLayoutEffect(() => {
    if (!desktopShowPanel) return;

    const updateRect = () => {
      const node = containerRef.current;
      if (!node) return;
      const r = node.getBoundingClientRect();
      setPanelRect({
        top: r.bottom + 8,
        left: r.left,
        width: r.width,
      });
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [desktopShowPanel]);

  // Outside-click closes the desktop dropdown. Check both the input container
  // and the portal-rendered panel so clicking a result wins the race against
  // this handler — the panel lives at document.body, not inside containerRef.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inInput = containerRef.current?.contains(target);
      const inPanel = (target as Element)?.closest?.("#global-search-listbox");
      if (!inInput && !inPanel) {
        setDesktopOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const closeAll = () => {
    setDesktopOpen(false);
    setMobileOpen(false);
    setValue("");
    setActiveIndex(0);
  };

  const go = (item: SearchItem) => {
    closeAll();
    navigate(item.url);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (value) {
        setValue("");
        setActiveIndex(0);
      } else {
        setDesktopOpen(false);
        setMobileOpen(false);
        e.currentTarget.blur();
      }
      return;
    }
    if (flatItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatItems.length) % flatItems.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const picked = flatItems[activeIndex];
      if (picked) go(picked.item);
    }
  };

  // Single source of truth for what the panel body looks like — re-used by
  // both the desktop portal dropdown and the mobile dialog.
  const renderResults = () => (
    <>
      {value.trim().length < 2 && (
        <div className="px-4 py-3 text-sm text-gray-500">
          Keep typing — at least 2 characters.
        </div>
      )}

      {value.trim().length >= 2 && isLoading && groups.length === 0 && (
        <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          Searching…
        </div>
      )}

      {value.trim().length >= 2 && isError && (
        <div className="px-4 py-3 text-sm text-red-600">
          {errorMessage || "Search failed. Try again."}
        </div>
      )}

      {value.trim().length >= 2 && !isLoading && !isError && isEmpty && (
        <div className="px-4 py-3 text-sm text-gray-500">
          No matches for <span className="font-medium">"{value.trim()}"</span>.
        </div>
      )}

      {groups.map((group) => (
        <div key={group.key} className="border-b border-gray-50 last:border-b-0">
          <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50/60">
            {group.label}
          </div>
          <ul>
            {group.items.map((item) => {
              const fi = flatItems.findIndex(
                (f) => f.group.key === group.key && f.item.id === item.id,
              );
              const isActive = fi === activeIndex;
              return (
                <li key={`${group.key}-${item.id}`} role="option" aria-selected={isActive}>
                  <button
                    type="button"
                    onMouseEnter={() => fi !== -1 && setActiveIndex(fi)}
                    onClick={() => go(item)}
                    className={cn(
                      "w-full text-left px-4 py-2 text-sm flex flex-col gap-0.5 cursor-pointer",
                      isActive ? "bg-brand-700/5" : "hover:bg-gray-50",
                    )}
                  >
                    <span className="font-medium text-gray-900 truncate">{item.title}</span>
                    {item.subtitle && (
                      <span className="text-xs text-gray-500 truncate">{item.subtitle}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );

  const desktopPanel = desktopShowPanel && panelRect && (
    <div
      id="global-search-listbox"
      role="listbox"
      style={{
        position: "fixed",
        top: panelRect.top,
        left: panelRect.left,
        width: panelRect.width,
        maxHeight: "28rem",
      }}
      className="overflow-y-auto rounded-lg border border-gray-100 bg-white shadow-lg z-9999 animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-200"
    >
      {renderResults()}
    </div>
  );

  const onValueChange = (next: string) => {
    setValue(next);
    setActiveIndex(0);
  };

  return (
    <>
      {/* Desktop / tablet inline input. Stretches via flex-1; min-w-0 lets it
          shrink alongside the workspace selector instead of overflowing. */}
      <div
        ref={containerRef}
        className={cn(
          "group relative min-w-0 hidden md:block flex-1 transition-[max-width] duration-300 ease-out",
          desktopExpanded ? "max-w-full" : "max-w-10",
        )}
      >
        <Search
          className={cn(
            "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors text-brand-700",
            !desktopExpanded && "group-hover:text-brand-600",
          )}
        />
        <Input
          ref={desktopInputRef}
          /* type=search + a non-credential name + the vendor opt-out attrs stop
             browser/password-manager autofill, which was dumping the saved email
             into this field after navigating to the profile page. */
          type="search"
          name="skillify-global-search"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          /* Chrome ignores autoComplete="off" and re-fires autofill on every
             navigation (e.g. clicking the profile link), dumping the saved
             email here. A field that's readOnly until focused is skipped by
             autofill entirely; focus clears it so typing still works. */
          readOnly={!desktopOpen}
          placeholder={desktopExpanded ? placeholder : ""}
          value={value}
          onChange={(e) => {
            onValueChange(e.target.value);
            setDesktopOpen(true);
          }}
          onFocus={() => setDesktopOpen(true)}
          onKeyDown={onKeyDown}
          className={cn(
            "pl-10 border-none focus-visible:ring-0 focus-visible:border focus-visible:border-brand-700",
            "transition-[padding,background-color] duration-300 [&::-webkit-search-cancel-button]:appearance-none",
            desktopExpanded ? "pr-9 cursor-text bg-gray-50" : "pr-0 cursor-pointer bg-transparent",
          )}
          aria-label="Search"
          aria-autocomplete="list"
          aria-expanded={desktopShowPanel}
          aria-controls="global-search-listbox"
        />
        {isLoading && desktopExpanded && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
        )}
      </div>
      {desktopPanel && createPortal(desktopPanel, document.body)}

      {/* Mobile-only icon trigger. */}
      <button
        type="button"
        aria-label="Search"
        className="md:hidden cursor-pointer text-brand-700 hover:text-brand-600 transition-colors p-2"
        onClick={() => {
          setMobileOpen(true);
          setValue("");
          setActiveIndex(0);
        }}
      >
        <Search className="h-5 w-5" />
      </button>

      {/* Mobile search dialog. Top-aligned (Spotlight-style) instead of the
          shadcn default vertical centering — feels right for a search task. */}
      <Dialog
        open={mobileOpen}
        onOpenChange={(next) => {
          setMobileOpen(next);
          if (!next) {
            setValue("");
            setActiveIndex(0);
            setMobileFocused(false);
          }
        }}
      >
        <DialogContent
          className="md:hidden top-4 translate-y-0 sm:max-w-[95vw] p-3 gap-0 bg-white"
          showCloseButton
        >
          <DialogTitle className="sr-only">Search</DialogTitle>
          <DialogDescription className="sr-only">
            Search pages, courses, users, and other items you have access to.
          </DialogDescription>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-700 h-4 w-4" />
            <Input
              autoFocus
              type="search"
              name="skillify-global-search"
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              readOnly={!mobileFocused}
              placeholder={placeholder}
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              onFocus={() => setMobileFocused(true)}
              onKeyDown={onKeyDown}
              className="pl-10 pr-9 bg-gray-50 border-none focus-visible:ring-0 focus-visible:border focus-visible:border-brand-700 [&::-webkit-search-cancel-button]:appearance-none"
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>
          <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-lg border border-gray-100 bg-white">
            {value.trim().length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500 text-center">
                Start typing to search.
              </div>
            ) : (
              renderResults()
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
